import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAiChatStore } from "@/domains/ai/ai-chat/stores/ai-chat.store";
import {
  EXECUTE_AGENT_INTENT_MUTATION,
  GET_THREAD_INTENTS_QUERY,
  REJECT_AGENT_INTENT_MUTATION,
} from "@/graphql/ai-thread";
import { APPOINTMENT_QUERY } from "@/graphql/appointments";
import { CUSTOMER_QUERY } from "@/graphql/customers";
import { SERVICE_CATEGORIES_QUERY, SERVICES_QUERY } from "@/graphql/services";
import { WORKERS_QUERY } from "@/graphql/workers";
import { useSalonId, useSalonStore } from "@/lib/store/salon";
import { getSalonCurrencySymbol } from "@/lib/utils/currency";
import { type IntentDetailUiText, renderIntentDetails } from "./intent/intent-detail-renderer";
import { getOperationIcon, getOperationName, getStatusMeta } from "./intent/intent-metadata";
import { humanizePayload } from "./intent/payload-humanizer";
import { INTENT_TEMPLATES } from "./intent/templates";
import { TOOL_TO_ACTION_TYPE } from "./intent/tool-action-map";
import type { IntentActionType } from "./intent/types";
import { isRecord, safeParseJson } from "./intent/utils";

type IntentStatus = "PENDING" | "EXECUTED" | "REJECTED" | "FAILED";

const REFETCH_QUERIES_BY_ACTION: Record<string, string[]> = {
  MANAGE_SERVICE: ["ServicesWithCategories"],
  MANAGE_CATEGORY: ["ServicesWithCategories", "ServiceCategories"],
  SORT_ITEMS: ["ServicesWithCategories"],
  BATCH_UPDATE_SERVICES: ["ServicesWithCategories"],
  BULK_UPDATE_SERVICES: ["ServicesWithCategories"],
  COPY_SERVICES: ["ServicesWithCategories"],
  BATCH_CREATE_SERVICES: ["ServicesWithCategories"],
  BATCH_CREATE_CATEGORIES: ["ServicesWithCategories", "ServiceCategories"],
  BATCH_UPDATE_CATEGORIES: ["ServicesWithCategories", "ServiceCategories"],
  UPDATE_WORKER_SERVICES: ["ServicesWithCategories", "TeamMembers", "GetWorkerAllowedServices"],
  UPDATE_WORKER_SERVICE_RESTRICTIONS: [
    "ServicesWithCategories",
    "TeamMembers",
    "GetWorkerAllowedServices",
  ],
  CREATE_TEAM_MEMBER: ["TeamMembers"],
  UPDATE_TEAM_MEMBER: ["TeamMembers"],
  COMMIT_CONFIG_CHANGE: ["GetSalonConfig"],
  UPDATE_OPERATING_HOURS: ["GetSalonConfig"],
  UPDATE_SALON_PROFILE: ["GetSalonConfig"],
  UPDATE_SALON_CONTACT: ["GetSalonConfig"],
  CREATE_HOLIDAY: ["GetHolidays"],
  UPDATE_HOLIDAY: ["GetHolidays"],
  CREATE_APPOINTMENT: ["Appointments", "Appointment"],
  UPDATE_APPOINTMENT: ["Appointments", "Appointment"],
  BATCH_UPDATE_APPOINTMENTS: ["Appointments", "Appointment"],
  UPDATE_APPOINTMENT_STATUS: ["Appointments", "Appointment"],
  ASSIGN_APPOINTMENT_WORKER: ["Appointments", "Appointment"],
  CREATE_CUSTOMER: ["Customers"],
  CREATE_DISCOUNT: ["Discounts"],
  UPDATE_DISCOUNT: ["Discounts"],
  DELETE_DISCOUNT: ["Discounts"],
};

const parseIntentStatus = (value: unknown): IntentStatus | undefined => {
  if (value === "PENDING" || value === "EXECUTED" || value === "REJECTED" || value === "FAILED") {
    return value;
  }

  return undefined;
};

export const IntentRequest: ToolCallMessagePartComponent = ({ args, result, toolName }) => {
  const { t, i18n } = useTranslation("ai-chat");
  const client = useApolloClient();
  const salonId = useSalonId();
  const salonCurrency = useSalonStore((state) => state.currentSalon?.currency);
  const currencySymbol = getSalonCurrencySymbol(salonCurrency);
  const threadId = useAiChatStore((state) => state.threadId);
  const allMessages = useAiChatStore((state) => state.messages);
  const [localStatus, setLocalStatus] = useState<IntentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [executeIntent, { loading: isExecuting }] = useMutation(EXECUTE_AGENT_INTENT_MUTATION);
  const [rejectIntent, { loading: isRejecting }] = useMutation(REJECT_AGENT_INTENT_MUTATION);

  const parsedArgs = safeParseJson(args);
  const intentArgs = isRecord(parsedArgs) ? parsedArgs : {};

  const parsedResult = safeParseJson(result);
  const toolResult = isRecord(parsedResult) ? parsedResult : undefined;
  const isToolDone = result !== undefined;

  const actionTypeFromResult =
    typeof toolResult?.actionType === "string" && toolResult.actionType in INTENT_TEMPLATES
      ? (toolResult.actionType as IntentActionType)
      : undefined;
  const actionTypeFromTool = TOOL_TO_ACTION_TYPE[toolName];
  const actionType = actionTypeFromResult || actionTypeFromTool || "MANAGE_SERVICE";
  const _template = INTENT_TEMPLATES[actionType];

  const intentFieldLabels = useMemo<Record<string, string>>(
    () => ({
      // Shared
      serviceId: t("intent.fields.serviceId", "Service ID"),
      categoryId: t("intent.fields.categoryId", "Category ID"),
      name: t("intent.fields.name", "Name"),
      description: t("intent.fields.description", "Description"),
      allowOnlineBooking: t("intent.fields.allowOnlineBooking", "Online Booking"),
      isActive: t("intent.fields.isActive", "Active"),

      // Services
      serviceCode: t("intent.fields.serviceCode", "Service Code"),
      basePrice: t("intent.fields.basePrice", "Base Price"),
      duration: t("intent.fields.duration", "Duration (min)"),

      // Categories
      color: t("intent.fields.color", "Color"),
      icon: t("intent.fields.icon", "Icon"),
      isArchived: t("intent.fields.isArchived", "Archived"),

      // Sorting
      type: t("intent.fields.type", "Target Type"),
      items: t("intent.fields.items", "Sort Items"),

      // Bulk / copy
      serviceIds: t("intent.fields.serviceIds", "Service IDs"),
      targetCategoryId: t("intent.fields.targetCategoryId", "Target Category ID"),
      priceAdjustment: t("intent.fields.priceAdjustment", "Price Adjustment"),
      durationAdjustment: t("intent.fields.durationAdjustment", "Duration Adjustment"),
      categoryChange: t("intent.fields.categoryChange", "Move To Category"),
      archive: t("intent.fields.archive", "Archive"),
      namePrefix: t("intent.fields.namePrefix", "Name Prefix"),
      nameSuffix: t("intent.fields.nameSuffix", "Name Suffix"),

      // Worker restrictions
      workerId: t("intent.fields.workerId", "Worker"),
      workerSalonId: t("intent.fields.workerSalonId", "Worker Salon ID"),
      restrictionMode: t("intent.fields.restrictionMode", "Restriction Mode"),
      categoryIds: t("intent.fields.categoryIds", "Category IDs"),

      // Batch operations
      updates: t("intent.fields.updates", "Updates"),
      services: t("intent.fields.services", "Services"),
      categories: t("intent.fields.categories", "Categories"),

      // Appointments
      appointmentId: t("intent.fields.appointmentId", "Appointment"),
      appointmentIds: t("intent.fields.appointmentIds", "Appointments"),
      appointmentServiceId: t("intent.fields.appointmentServiceId", "Appointment Service"),
      customerId: t("intent.fields.customerId", "Customer"),
      startTime: t("intent.fields.startTime", "Start Time"),
      bookingStatus: t("intent.fields.bookingStatus", "Booking Status"),
      outcome: t("intent.fields.outcome", "Outcome"),
      notes: t("intent.fields.notes", "Notes"),
      internalNotes: t("intent.fields.internalNotes", "Internal Notes"),
      customerMessage: t("intent.fields.customerMessage", "Customer Message"),
      serviceSelections: t("intent.fields.serviceSelections", "Services"),
      action: t("intent.fields.action", "Action"),
      cancellationReasonType: t("intent.fields.cancellationReasonType", "Cancellation Reason"),
      cancellationReasonText: t("intent.fields.cancellationReasonText", "Cancellation Details"),

      // Customers
      firstName: t("intent.fields.firstName", "First name"),
      lastName: t("intent.fields.lastName", "Last name"),
      email: t("intent.fields.email", "Email"),
      phone: t("intent.fields.phone", "Phone"),

      // Config
      category: t("intent.fields.configCategory", "Category"),
      key: t("intent.fields.configKey", "Key"),
      value: t("intent.fields.value", "Value"),
      operatingHours: t("intent.fields.operatingHours", "Operating Hours"),
      contacts: t("intent.fields.contacts", "Contacts"),

      // Discounts
      discountId: t("intent.fields.discountId", "Discount"),
      discountTitle: t("intent.fields.discountTitle", "Discount"),
      input: t("intent.fields.input", "Input"),
      title: t("intent.fields.title", "Title"),
      applyTo: t("intent.fields.applyTo", "Apply To"),
      rules: t("intent.fields.rules", "Rules"),
      minSpent: t("intent.fields.minSpent", "Minimum Spend"),
      activePeriod: t("intent.fields.activePeriod", "Active Period"),
      targetedPeriod: t("intent.fields.targetedPeriod", "Booking Period"),
      bookingPeriod: t("intent.fields.bookingPeriod", "Booking Period"),
      weekdayCondition: t("intent.fields.weekdayCondition", "Weekday condition"),
      visibilityPeriod: t("intent.fields.visibilityPeriod", "Visibility period"),
      autoApply: t("intent.fields.autoApply", "Auto Apply"),
      // Salon profile
      address: t("intent.fields.address", "Address"),
      additionalAddressInfo: t("intent.fields.additionalAddressInfo", "Additional Address"),
      city: t("intent.fields.city", "City"),
      state: t("intent.fields.state", "State"),
      zipCode: t("intent.fields.zipCode", "ZIP Code"),
      country: t("intent.fields.country", "Country"),
      publicDescription: t("intent.fields.publicDescription", "Public Description"),

      // Holidays
      id: t("intent.fields.id", "ID"),
      message: t("intent.fields.message", "Message"),
      startDateTime: t("intent.fields.startDateTime", "Start"),
      endDateTime: t("intent.fields.endDateTime", "End"),
      blockOnlineBooking: t("intent.fields.blockOnlineBooking", "Block Online Booking"),
      scope: t("intent.fields.scope", "Scope"),

      // Team
      displayName: t("intent.fields.displayName", "Display Name"),
      salonRole: t("intent.fields.salonRole", "Role"),
      canAcceptOnlineBookings: t(
        "intent.fields.canAcceptOnlineBookings",
        "Can Accept Online Bookings"
      ),
      bio: t("intent.fields.bio", "Bio"),
    }),
    [t]
  );

  const intentDetailUiText = useMemo<IntentDetailUiText>(
    () => ({
      noDetailsProvided: t("intent.misc.noDetailsProvided", "No details provided."),
      sections: {
        target: t("intent.sections.target", "Target"),
        fieldsToApply: t("intent.sections.fieldsToApply", "Fields To Apply"),
        payload: t("intent.sections.payload", "Payload"),
        sortTarget: t("intent.sections.sortTarget", "Sort Target"),
        newOrder: t("intent.sections.newOrder", "New Order"),
        serviceUpdates: t("intent.sections.serviceUpdates", "Service Updates"),
        targetScope: t("intent.sections.targetScope", "Target Scope"),
        bulkChanges: t("intent.sections.bulkChanges", "Bulk Changes"),
        copyScope: t("intent.sections.copyScope", "Copy Scope"),
        copyModifiers: t("intent.sections.copyModifiers", "Copy Modifiers"),
        restrictionPlan: t("intent.sections.restrictionPlan", "Restriction Plan"),
        newServices: t("intent.sections.newServices", "New Services"),
        newCategories: t("intent.sections.newCategories", "New Categories"),
        operatingHours: t("intent.sections.operatingHours", "Operating Hours"),
        assignment: t("intent.sections.assignment", "Assignment"),
        optionGroups: t("intent.sections.optionGroups", "Option Groups"),
      },
      table: {
        newOrder: t("intent.table.newOrder", "New Order"),
        itemId: t("intent.table.itemId", "Item ID"),
        index: t("intent.table.index", "#"),
        name: t("intent.table.name", "Name"),
        serviceCode: t("intent.table.serviceCode", "Service Code"),
        categoryId: t("intent.table.categoryId", "Category ID"),
        basePrice: t("intent.table.basePrice", "Base Price"),
        duration: t("intent.table.duration", "Duration"),
        description: t("intent.table.description", "Description"),
        color: t("intent.table.color", "Color"),
        onlineBooking: t("intent.table.onlineBooking", "Online Booking"),
      },
      labels: {
        serviceIds: t("intent.misc.serviceIds", "Service IDs"),
        categoryIds: t("intent.misc.categoryIds", "Category IDs"),
        closed: t("intent.values.closed", "Closed"),
        unassigned: t("intent.values.unassigned", "Unassigned"),
        discountApplyToTotal: t("intent.values.discount.applyTo.total", "Total"),
        discountApplyToPerItem: t("intent.values.discount.applyTo.per_item", "Per item"),
        serviceItemTitle: (index: number) =>
          t("intent.misc.serviceItemTitle", "Service {{index}}", { index }),
        serviceFallback: t("intent.fields.serviceFallback", "Service"),
        workerFallback: t("intent.fields.workerFallback", "Worker"),
      },
    }),
    [t]
  );

  const { data: servicesData } = useQuery(SERVICES_QUERY, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-first",
  });

  const { data: categoriesData } = useQuery(SERVICE_CATEGORIES_QUERY, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const serviceNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const services = servicesData?.services;
    if (!Array.isArray(services)) {
      return map;
    }

    for (const service of services) {
      if (service?.id && service?.name) {
        map.set(service.id, service.name);
      }
    }

    return map;
  }, [servicesData]);

  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const categories = categoriesData?.serviceCategories;
    if (!Array.isArray(categories)) {
      return map;
    }

    for (const category of categories) {
      if (category?.id && category?.name) {
        map.set(category.id, category.name);
      }
    }

    return map;
  }, [categoriesData]);

  const { data: workersData } = useQuery(WORKERS_QUERY, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-first",
  });

  const workerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const workers = workersData?.workers?.workers;
    if (!Array.isArray(workers)) {
      return map;
    }

    for (const worker of workers) {
      if (!worker?.id) continue;
      const firstName = worker?.user?.firstName;
      const lastName = worker?.user?.lastName;
      const userName =
        typeof firstName === "string" || typeof lastName === "string"
          ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
          : "";
      const displayName = userName.length > 0 ? userName : worker.displayName;
      if (typeof displayName === "string" && displayName.length > 0) {
        map.set(worker.id, displayName);
      }
    }

    return map;
  }, [workersData]);

  const intentId = typeof toolResult?.intentId === "string" ? toolResult.intentId : undefined;

  const { data: intentsData, refetch: refetchIntents } = useQuery(GET_THREAD_INTENTS_QUERY, {
    variables: { threadId, salonId },
    skip: !threadId || !salonId || !intentId,
    // We rely on intent status to be durable across UI remounts.
    // `cache-first` can resurrect stale "PENDING" cards until a full page refresh.
    fetchPolicy: "cache-and-network",
  });

  const persistedStatus = useMemo(() => {
    const intents = intentsData?.getThreadIntents;
    if (!Array.isArray(intents) || !intentId) {
      return undefined;
    }

    const matchedIntent = intents.find((intent) => intent?.id === intentId);
    return parseIntentStatus(matchedIntent?.status);
  }, [intentsData, intentId]);

  // When the LLM calls `execute_intent` itself, the chat panel only sees a tool-call
  // message — Apollo's intent query is not automatically re-fetched. Walk the message
  // list for an `execute_intent` (or `reject_intent`) tool result that targets this
  // card's intentId, and use its result to drive the status.
  const executionStatus = useMemo<IntentStatus | undefined>(() => {
    if (!intentId) return undefined;

    for (const message of allMessages) {
      if (message.role !== "assistant") continue;
      const content = message.content;
      if (typeof content === "string") continue;

      for (const part of content) {
        if (part.type !== "tool-call") continue;
        if (part.toolName !== "execute_intent" && part.toolName !== "reject_intent") continue;

        const partArgs = isRecord(part.args) ? part.args : safeParseJson(part.argsText);
        const argsRecord = isRecord(partArgs) ? partArgs : undefined;
        if (argsRecord?.intentId !== intentId) continue;

        const partResultRaw = (part as { result?: unknown }).result;
        if (partResultRaw === undefined) continue;

        const parsedResult = safeParseJson(partResultRaw);
        if (!isRecord(parsedResult)) continue;

        if (typeof parsedResult.error === "string" && parsedResult.error.length > 0) {
          return "FAILED";
        }

        const status =
          parseIntentStatus(parsedResult.intentStatus) ?? parseIntentStatus(parsedResult.status);
        if (status) return status;
      }
    }

    return undefined;
  }, [allMessages, intentId]);

  // Once we detect the LLM-driven execution, refetch the same queries the user-click
  // path refetches: the intent list (so persistedStatus catches up) and the domain
  // queries (so the rest of the UI reflects the new state immediately).
  const refetchedForExecutionRef = useRef(false);
  useEffect(() => {
    if (!executionStatus || executionStatus === "PENDING") return;
    if (refetchedForExecutionRef.current) return;
    refetchedForExecutionRef.current = true;

    void refetchIntents();

    const queriesToRefetch = REFETCH_QUERIES_BY_ACTION[actionType];
    if (queriesToRefetch?.length) {
      void client.refetchQueries({ include: queriesToRefetch });
    }
  }, [executionStatus, refetchIntents, client, actionType]);

  const statusFromToolResult =
    parseIntentStatus(toolResult?.intentStatus) ?? parseIntentStatus(toolResult?.status);

  const currentStatus: IntentStatus =
    localStatus ?? executionStatus ?? persistedStatus ?? statusFromToolResult ?? "PENDING";

  const payloadToDisplayRaw = isRecord(toolResult?.payload) ? toolResult.payload : intentArgs;

  const customerIdForLookup =
    typeof payloadToDisplayRaw.customerId === "string" ? payloadToDisplayRaw.customerId : undefined;

  const shouldFetchCustomerForDisplay = actionType === "CREATE_APPOINTMENT";

  const { data: customerData } = useQuery(CUSTOMER_QUERY, {
    variables: { salonId, customerId: customerIdForLookup as string },
    skip: !salonId || !customerIdForLookup || !shouldFetchCustomerForDisplay,
    fetchPolicy: "cache-first",
  });

  const customerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const customer = customerData?.customer;
    if (!customerIdForLookup || !customer) {
      return map;
    }

    const firstName = customer.firstName;
    const lastName = customer.lastName;
    const displayName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    if (displayName.length > 0) {
      map.set(customerIdForLookup, displayName);
    }

    return map;
  }, [customerData, customerIdForLookup]);

  const appointmentIdForLookup =
    typeof payloadToDisplayRaw.appointmentId === "string"
      ? payloadToDisplayRaw.appointmentId
      : undefined;

  const shouldFetchAppointmentForDisplay =
    actionType === "ASSIGN_APPOINTMENT_WORKER" ||
    actionType === "UPDATE_APPOINTMENT" ||
    actionType === "UPDATE_APPOINTMENT_STATUS";

  const { data: appointmentData } = useQuery(APPOINTMENT_QUERY, {
    variables: { salonId, id: appointmentIdForLookup as string },
    skip: !salonId || !appointmentIdForLookup || !shouldFetchAppointmentForDisplay,
    fetchPolicy: "cache-first",
  });

  const appointmentServiceNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const appointmentServices = appointmentData?.appointment?.appointmentServices;
    if (!Array.isArray(appointmentServices)) {
      return map;
    }

    for (const service of appointmentServices) {
      if (!service?.id) continue;
      const name =
        typeof service.serviceName === "string" && service.serviceName.length > 0
          ? service.serviceName
          : undefined;
      if (name) {
        map.set(service.id, name);
      }
    }

    return map;
  }, [appointmentData]);

  const payloadToDisplay = useMemo(
    () =>
      humanizePayload(payloadToDisplayRaw, actionType, serviceNameMap, categoryNameMap, {
        workerNameMap,
        appointmentServiceNameMap,
        customerNameMap,
      }),
    [
      payloadToDisplayRaw,
      actionType,
      serviceNameMap,
      categoryNameMap,
      workerNameMap,
      appointmentServiceNameMap,
      customerNameMap,
    ]
  );

  const translateForMetadata = (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => t(key, { defaultValue, ...(options ?? {}) });

  const operationName = getOperationName(
    actionType,
    payloadToDisplay,
    toolResult,
    translateForMetadata
  );
  const operationIcon = getOperationIcon(actionType, payloadToDisplay, toolResult);
  const statusMeta = getStatusMeta(currentStatus, isToolDone, {
    approved: t("intent.status.approved", "Approved"),
    rejected: t("intent.status.rejected", "Rejected"),
    failed: t("intent.status.failed", "Failed"),
    creating: t("intent.status.creating", "Creating"),
    pending: t("intent.status.pending", "Pending"),
  });

  const handleApprove = async () => {
    if (!intentId || currentStatus !== "PENDING") return;
    setError(null);

    try {
      const response = await executeIntent({
        variables: { intentId, salonId },
      });

      if (response.errors && response.errors.length > 0) {
        setError(response.errors[0]?.message || "Failed to execute intent");
        setLocalStatus("FAILED");
      } else {
        const nextStatus =
          parseIntentStatus(response.data?.executeAgentIntent?.status) ?? "EXECUTED";
        setLocalStatus(nextStatus);

        // Persist status into Apollo cache so remounts don't show stale "PENDING".
        await refetchIntents();

        // Refetch domain queries so the UI updates immediately.
        const queriesToRefetch = REFETCH_QUERIES_BY_ACTION[actionType];
        if (queriesToRefetch?.length) {
          client.refetchQueries({ include: queriesToRefetch });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to execute intent");
      setLocalStatus("FAILED");
    }
  };

  const handleReject = async () => {
    if (!intentId || currentStatus !== "PENDING") return;
    setError(null);

    try {
      const response = await rejectIntent({
        variables: {
          intentId,
          salonId,
          reason: "Rejected by user",
        },
      });

      if (response.errors && response.errors.length > 0) {
        setError(response.errors[0]?.message || "Failed to reject intent");
      } else {
        const nextStatus =
          parseIntentStatus(response.data?.rejectAgentIntent?.status) ?? "REJECTED";
        setLocalStatus(nextStatus);

        // Persist status into Apollo cache so remounts don't show stale "PENDING".
        await refetchIntents();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject intent");
    }
  };

  const isSubmitting = isExecuting || isRejecting;

  const cardToneClass =
    currentStatus === "EXECUTED"
      ? "border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-950/20"
      : currentStatus === "REJECTED" || currentStatus === "FAILED"
        ? "border-red-200 bg-red-50 dark:border-red-900/30 dark:bg-red-950/20"
        : "border-blue-200 bg-blue-50 dark:border-blue-900/30 dark:bg-blue-950/20";

  return (
    <Card className={`my-2 max-w-3xl ${cardToneClass}`}>
      <CardHeader className="p-3">
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <div className="flex items-center gap-2">
            {operationIcon}
            <span>{operationName}</span>
          </div>
          <div
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${statusMeta.className}`}
          >
            {statusMeta.icon}
            <span>{statusMeta.text}</span>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-3 pt-0 space-y-4">
        {error && <div className="text-xs text-red-500">{error}</div>}
        {renderIntentDetails(
          actionType,
          payloadToDisplay,
          intentFieldLabels,
          i18n.language,
          currencySymbol,
          intentDetailUiText
        )}
      </CardContent>

      {currentStatus === "PENDING" && isToolDone && (
        <CardFooter className="flex justify-end gap-2 border-t border-blue-200/50 pt-3 dark:border-blue-900/30">
          <Button variant="outline" size="sm" onClick={handleReject} disabled={isSubmitting}>
            {t("intent.actions.reject", "Reject")}
          </Button>
          <Button
            variant="default"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleApprove}
            disabled={isSubmitting}
          >
            {isExecuting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="mr-2 h-4 w-4" />
            )}
            {t("intent.actions.apply", "Apply")}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};
